import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserGroupIcon,
  CalendarIcon,
  DocumentTextIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const AdminPrograms = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('signups');
  const [signups, setSignups] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSignup, setSelectedSignup] = useState(null);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    session_date: '',
    attended: true,
    notes: ''
  });
  const [calendarAttendance, setCalendarAttendance] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showMarkAttendanceModal, setShowMarkAttendanceModal] = useState(false);
  const [selectedDateForAttendance, setSelectedDateForAttendance] = useState(null);
  const [usersWithTermSignups, setUsersWithTermSignups] = useState([]);
  const [showTermInvoiceModal, setShowTermInvoiceModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [signupSearchQuery, setSignupSearchQuery] = useState('');
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [markedAttendance, setMarkedAttendance] = useState(new Set());
  const [processingAttendance, setProcessingAttendance] = useState(new Set());
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      if (activeTab === 'signups') {
        const response = await fetch(`${apiUrl}/api/programs/admin/signups`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSignups(data.signups || []);
        }
      } else if (activeTab === 'attendance') {
        // Fetch calendar data
        const startDate = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0];
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const endDate = lastDay.toISOString().split('T')[0];
        
        const response = await fetch(`${apiUrl}/api/programs/admin/attendance/by-date?start_date=${startDate}&end_date=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCalendarAttendance(data.attendance || {});
        }
        
        // Also fetch traditional attendance list
        const responseList = await fetch(`${apiUrl}/api/programs/admin/attendance`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (responseList.ok) {
          const dataList = await responseList.json();
          setAttendance(dataList.attendance || []);
        }
      } else if (activeTab === 'invoices') {
        const response = await fetch(`${apiUrl}/api/programs/invoices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setInvoices(data.invoices || []);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, activeTab, currentYear, currentMonth]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, activeTab, fetchData]);

  const markAttendance = async () => {
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/programs/admin/attendance/mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          signup_id: selectedSignup.id,
          ...attendanceForm
        })
      });

      if (response.ok) {
        alert('Attendance marked successfully');
        setShowAttendanceModal(false);
        setSelectedSignup(null);
        setAttendanceForm({ session_date: '', attended: true, notes: '' });
        fetchData();
      } else {
        alert('Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const toggleInvoiceStatus = async (invoiceId, currentStatus) => {
    const newStatus = currentStatus === 'paid' ? 'unpaid' : 'paid';
    
    if (!window.confirm(`Change invoice status from "${currentStatus}" to "${newStatus}"?`)) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/${invoiceId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchData();
      } else {
        alert('Failed to update invoice status');
      }
    } catch (error) {
      console.error('Error toggling invoice status:', error);
      alert('Failed to update invoice status');
    }
  };

  const approveAndSendInvoice = async (invoiceId) => {
    if (!window.confirm('Approve this invoice and send email to user?')) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invoice_ids: [invoiceId] })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        fetchData();
      } else {
        alert('Failed to approve invoice');
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      alert('Failed to approve invoice');
    }
  };

  const deleteAllSignups = async () => {
    if (!window.confirm('⚠️ DANGER: This will delete ALL signups, attendance records, and invoices. Are you absolutely sure?')) {
      return;
    }
    
    if (!window.confirm('This action cannot be undone. Type "DELETE" to confirm:')) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/delete-all-signups`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        fetchData(); // Refresh signups list
      } else {
        const error = await response.json();
        alert(`❌ ${error.error || 'Failed to delete all signups'}`);
      }
    } catch (error) {
      console.error('Error deleting all signups:', error);
      alert('❌ Failed to delete all signups');
    }
  };

  const toggleSignupStatus = async (signupId, currentStatus) => {
    // Determine the next status based on current status
    let newStatus;
    if (currentStatus === 'active') {
      newStatus = 'inactive';
    } else if (currentStatus === 'inactive') {
      newStatus = 'active';
    } else if (currentStatus === 'cancelled') {
      newStatus = 'active';
    } else {
      newStatus = 'active';
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/signups/${signupId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchData(); // Refresh signups list
      } else {
        const error = await response.json();
        alert(`Failed to toggle signup status: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling signup status:', error);
      alert('Failed to toggle signup status');
    }
  };

  const cancelInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to cancel this invoice? The invoice will be marked as cancelled.')) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/${invoiceId}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✅ Invoice cancelled successfully');
        fetchData(); // Refresh the list
      } else {
        const data = await response.json();
        alert(`❌ Failed to cancel invoice: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      alert('❌ Failed to cancel invoice. Please try again.');
    }
  };

  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('⚠️ DANGER: This will permanently delete this invoice. This action cannot be undone. Are you sure?')) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✅ Invoice deleted successfully');
        fetchData(); // Refresh the list
      } else {
        const data = await response.json();
        alert(`❌ Failed to delete invoice: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('❌ Failed to delete invoice. Please try again.');
    }
  };

  const deleteAllInvoices = async () => {
    if (!window.confirm('⚠️ DANGER: This will delete ALL invoices and related payments. Are you absolutely sure?')) {
      return;
    }
    
    if (!window.confirm('This action cannot be undone. Type "DELETE" to confirm:')) {
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/admin/delete-all-invoices`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        fetchData(); // Refresh invoices list
      } else {
        const error = await response.json();
        alert(`❌ ${error.error || 'Failed to delete all invoices'}`);
      }
    } catch (error) {
      console.error('Error deleting all invoices:', error);
      alert('❌ Failed to delete all invoices');
    }
  };

  const handleCalendarDayClick = (dateStr) => {
    setSelectedDateForAttendance(dateStr);
    setShowMarkAttendanceModal(true);
  };

  const markAttendanceFromCalendar = async (signupId, dateStr, attended) => {
    // Prevent multiple clicks
    const markKey = `${signupId}-${dateStr}-${attended}`;
    if (markedAttendance.has(markKey) || processingAttendance.has(markKey)) {
      return;
    }
    
    try {
      setMarkingAttendance(true);
      setProcessingAttendance(prev => new Set(prev).add(markKey));
      
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/programs/admin/attendance/mark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          signup_id: signupId,
          session_date: dateStr,
          attended: attended,
          notes: ''
        })
      });

      if (response.ok) {
        // Mark as successfully processed - keep track to prevent duplicates
        setMarkedAttendance(prev => new Set(prev).add(markKey));
        
        // Don't remove from processing set until after fetch completes
        fetchData().finally(() => {
          setProcessingAttendance(prev => {
            const newSet = new Set(prev);
            newSet.delete(markKey);
            return newSet;
          });
        });
      } else {
        // Remove from processing set on error
        setProcessingAttendance(prev => {
          const newSet = new Set(prev);
          newSet.delete(markKey);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      // Remove from processing set on error
      setProcessingAttendance(prev => {
        const newSet = new Set(prev);
        newSet.delete(markKey);
        return newSet;
      });
    } finally {
      setMarkingAttendance(false);
    }
  };

  const generateInvoices = async () => {
    // Default to current month
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const month = prompt(`Enter month (YYYY-MM format):`, currentMonth);
    if (!month) return;

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ month })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully generated ${data.invoices_generated} invoices`);
        fetchData();
      } else {
        alert('Failed to generate invoices');
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      alert('Failed to generate invoices');
    }
  };

  const fetchUsersWithTermSignups = async () => {
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/users-with-term-signups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUsersWithTermSignups(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users with term signups:', error);
    }
  };

  const generateTermInvoices = async () => {
    // Show modal to select user
    await fetchUsersWithTermSignups();
    setShowTermInvoiceModal(true);
  };

  const handleGenerateTermInvoiceForUser = async () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    // Default to current month
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const month = prompt(`Enter term period (YYYY-MM format):`, currentMonth);
    if (!month) return;

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

      const response = await fetch(`${apiUrl}/api/programs/admin/invoices/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ month, invoice_type: 'term', user_id: selectedUserId })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully generated ${data.invoices_generated} term invoice${data.invoices_generated !== 1 ? 's' : ''}`);
        fetchData();
        setShowTermInvoiceModal(false);
        setSelectedUserId('');
      } else {
        const error = await response.json();
        alert(`Failed to generate term invoice: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating term invoices:', error);
      alert('Failed to generate term invoices');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Program Management</h1>

        <div className="border-b border-gray-200 mb-4 sm:mb-6">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('signups')}
              className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'signups' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserGroupIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
              Signups ({signups.length})
            </button>
            <button
              onClick={() => setActiveTab('attendance')}
              className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'attendance' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
              Attendance ({attendance.length})
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'invoices' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
              Invoices ({invoices.length})
            </button>
          </nav>
        </div>

        {activeTab === 'signups' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Program Signups</h2>
              <div className="flex gap-3">
                <button
                  onClick={deleteAllSignups}
                  className="bg-red-800 text-white px-4 py-2 rounded-lg hover:bg-red-900 flex items-center gap-2"
                >
                  <TrashIcon className="w-5 h-5" />
                  Delete ALL Signups
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by name, program, or email..."
                  value={signupSearchQuery}
                  onChange={(e) => setSignupSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {signups.length === 0 ? (
                <p className="text-gray-500">No signups found</p>
              ) : (
                <div className="space-y-4">
                  {signups
                    .filter(signup => {
                      if (!signupSearchQuery) return true;
                      const query = signupSearchQuery.toLowerCase();
                      return (
                        signup.participant_name?.toLowerCase().includes(query) ||
                        signup.program_name?.toLowerCase().includes(query) ||
                        signup.parent_email?.toLowerCase().includes(query) ||
                        signup.parent_name?.toLowerCase().includes(query)
                      );
                    })
                    .map((signup) => (
                      <div key={signup.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{signup.participant_name}</h3>
                            <p className="text-sm text-gray-500">{signup.program_name || 'Unknown Program'}</p>
                            <p className="text-sm text-gray-500">Age: {signup.participant_age}</p>
                            <p className="text-sm text-gray-500">Parent: {signup.parent_email}</p>
                          </div>
                          <div className="text-right">
                            <button
                              onClick={() => toggleSignupStatus(signup.id, signup.status)}
                              className={`px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity ${
                                signup.status === 'active' ? 'bg-green-100 text-green-800' :
                                signup.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                signup.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                              title="Click to toggle status"
                            >
                              {signup.status || 'unknown'}
                              <PencilIcon className="w-3 h-3" />
                            </button>
                            <p className="text-sm text-gray-500 mt-2">${signup.amount} - {signup.fee_type}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-3 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Attendance Calendar</h2>
              <div className="flex gap-2 sm:gap-3 items-center">
                <button
                  onClick={() => {
                    if (currentMonth === 0) {
                      setCurrentMonth(11);
                      setCurrentYear(currentYear - 1);
                    } else {
                      setCurrentMonth(currentMonth - 1);
                    }
                    fetchData();
                  }}
                  className="px-3 py-2 sm:py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm sm:text-base"
                >
                  ← Prev
                </button>
                <span className="px-3 sm:px-4 py-1 text-sm sm:text-base text-gray-700 font-medium">
                  {new Date(currentYear, currentMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => {
                    if (currentMonth === 11) {
                      setCurrentMonth(0);
                      setCurrentYear(currentYear + 1);
                    } else {
                      setCurrentMonth(currentMonth + 1);
                    }
                    fetchData();
                  }}
                  className="px-3 py-2 sm:py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm sm:text-base"
                >
                  Next →
                </button>
              </div>
            </div>
            <div className="p-2 sm:p-6">
              {/* Mobile View - List of days */}
              <div className="block sm:hidden space-y-2">
                {Array.from({ length: new Date(currentYear, currentMonth, 0).getDate() }, (_, i) => {
                  const date = new Date(currentYear, currentMonth, i + 1);
                  const dateStr = date.toISOString().split('T')[0];
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                  const dayAttendance = calendarAttendance[dateStr] || [];
                  const isToday = date.getDate() === new Date().getDate() && currentMonth === new Date().getMonth();
                  
                  return (
                    <div
                      key={i}
                      onClick={() => handleCalendarDayClick(dateStr)}
                      className={`border-2 rounded-lg p-3 active:scale-98 transition-all cursor-pointer ${
                        isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {dayName}, {i + 1}
                          </div>
                          <div className="text-xs text-gray-500">
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {dayAttendance.length} {dayAttendance.length === 1 ? 'record' : 'records'}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {dayAttendance.length > 0 ? (
                          dayAttendance.map((att, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded ${
                                att.attended ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {att.participant_name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Tap to mark</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Desktop View - Calendar Grid */}
              <div className="hidden sm:grid grid-cols-7 gap-2 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
                {Array.from({ length: new Date(currentYear, currentMonth, 0).getDate() }, (_, i) => {
                  const date = new Date(currentYear, currentMonth, i + 1);
                  const dateStr = date.toISOString().split('T')[0];
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const isWeekend = dayName === 'Sun' || dayName === 'Sat';
                  const dayAttendance = calendarAttendance[dateStr] || [];
                  
                  return (
                    <div
                      key={i}
                      onClick={() => handleCalendarDayClick(dateStr)}
                      className={`border rounded-lg p-2 min-h-[100px] cursor-pointer hover:bg-gray-50 transition-colors ${
                        isWeekend ? 'bg-gray-50' : 'bg-white'
                      } ${date.getDate() === new Date().getDate() && currentMonth === new Date().getMonth() ? 'border-blue-500 border-2' : ''}`}
                    >
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {i + 1}
                      </div>
                      <div className="space-y-1">
                        {dayAttendance.length > 0 ? (
                          dayAttendance.map((att, idx) => (
                            <div
                              key={idx}
                              className={`text-xs p-1 rounded ${
                                att.attended ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                              title={att.notes}
                            >
                              {att.participant_name}
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-gray-400">Click</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-3 sm:px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-0">Invoices</h2>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                  onClick={generateInvoices}
                  className="text-xs sm:text-sm bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-700 whitespace-nowrap"
                >
                  Generate Monthly
                </button>
                <button
                  onClick={generateTermInvoices}
                  className="text-xs sm:text-sm bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 whitespace-nowrap"
                >
                  Generate Term
                </button>
                <button
                  onClick={deleteAllInvoices}
                  className="text-xs sm:text-sm bg-red-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-900 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                >
                  <TrashIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  Delete ALL
                </button>
              </div>
            </div>
            <div className="p-6">
              {invoices.length === 0 ? (
                <p className="text-gray-500">No invoices found</p>
              ) : (
                <div>
                  <div className="flex items-start gap-2 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">Tips:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Click on any invoice row to view detailed information</li>
                        <li>Click on the status badge to toggle between paid/unpaid</li>
                      </ul>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Email</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoices.map((invoice) => (
                        <React.Fragment key={invoice.id}>
                          <tr 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              const newExpanded = new Set(expandedInvoices);
                              if (newExpanded.has(invoice.id)) {
                                newExpanded.delete(invoice.id);
                              } else {
                                newExpanded.add(invoice.id);
                              }
                              setExpandedInvoices(newExpanded);
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {expandedInvoices.has(invoice.id) ? (
                                  <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                                )}
                                <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                                {(invoice.children_details && invoice.children_details.length > 0) && (
                                  <span className="text-xs text-blue-600">({invoice.children_details.length} {invoice.children_details.length > 1 ? 'children' : 'child'})</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{invoice.billing_month}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 capitalize">{invoice.invoice_type || 'monthly'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{invoice.user_email || 'N/A'}</div>
                              {invoice.user_name && (
                                <div className="text-xs text-gray-400">{invoice.user_name}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="text-sm font-semibold text-gray-900">${invoice.amount?.toFixed(2) || '0.00'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => toggleInvoiceStatus(invoice.id, invoice.status)}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity ${
                                  invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                  invoice.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' :
                                  invoice.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                {invoice.status || 'unknown'}
                                {invoice.status !== 'cancelled' && <PencilIcon className="w-3 h-3" title="Click to toggle status" />}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-2">
                                {!invoice.approved && invoice.status !== 'cancelled' ? (
                                  <button
                                    onClick={() => approveAndSendInvoice(invoice.id)}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Approve & Send
                                  </button>
                                ) : invoice.approved ? (
                                  <span className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded-lg">
                                    ✓ Approved
                                  </span>
                                ) : invoice.status === 'cancelled' ? (
                                  <span className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg">
                                    Cancelled
                                  </span>
                                ) : null}
                                <button
                                  onClick={() => cancelInvoice(invoice.id)}
                                  className="px-2 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
                                  title="Cancel invoice"
                                  disabled={invoice.status === 'cancelled' || invoice.status === 'paid'}
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteInvoice(invoice.id)}
                                  className="px-2 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                  title="Delete invoice permanently"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedInvoices.has(invoice.id) && (
                            <tr>
                              <td colSpan="7" className="px-6 py-4 bg-gray-50">
                                <div className="space-y-4">
                                  {invoice.children_details && invoice.children_details.length > 0 ? (
                                    invoice.children_details.map((child, idx) => (
                                      <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                                        <div className="flex justify-between items-start mb-2">
                                          <div>
                                            <p className="font-semibold text-gray-900">{child.participant_name}</p>
                                            <p className="text-sm text-gray-600">{child.program_name}</p>
                                          </div>
                                          <p className="text-sm font-semibold text-gray-900">${child.amount.toFixed(2)}</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                                          <div>
                                            {(child.fee_type && child.fee_type.toLowerCase().includes('term')) || child.per_lesson_rate > 500 ? (
                                              <>
                                                <p><strong>Fee Type:</strong> Term Fee (Fixed)</p>
                                                <p><strong>Total Amount:</strong> ${child.amount.toFixed(2)}</p>
                                              </>
                                            ) : child.attendance_count > 0 ? (
                                              <>
                                                <p><strong>Attendance:</strong> {child.attendance_count} {child.attendance_count === 1 ? 'session' : 'sessions'}</p>
                                                <p><strong>Rate:</strong> ${child.per_lesson_rate}/session</p>
                                              </>
                                            ) : (
                                              <p><strong>Fee Type:</strong> Fixed rate</p>
                                            )}
                                          </div>
                                          {child.attendance_dates && child.attendance_dates.length > 0 && (
                                            <div>
                                              <p className="font-medium mb-1">Dates Attended:</p>
                                              <p className="text-xs">{child.attendance_dates.map(date => 
                                                new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                              ).join(', ')}</p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                      <p className="text-sm text-gray-600">No detailed information available</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showAttendanceModal && selectedSignup && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-semibold mb-4">Mark Attendance</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date (YYYY-MM-DD)
                  </label>
                  <input
                    type="date"
                    value={attendanceForm.session_date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, session_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={attendanceForm.attended}
                      onChange={(e) => setAttendanceForm({...attendanceForm, attended: e.target.checked})}
                      className="mr-2"
                    />
                    Attended
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={attendanceForm.notes}
                    onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={markAttendance}
                  className="flex-1 bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {showMarkAttendanceModal && selectedDateForAttendance && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-4 border-b">
                <h3 className="text-lg sm:text-xl font-semibold">Mark Attendance for {selectedDateForAttendance}</h3>
                <button
                  onClick={() => setShowMarkAttendanceModal(false)}
                  className="text-gray-500 hover:text-gray-700 sm:hidden"
                >
                  ✕
                </button>
              </div>
              
              {/* Search bar for attendance */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by child name..."
                  value={attendanceSearchQuery}
                  onChange={(e) => setAttendanceSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div className="space-y-4">
                {signups
                  .filter(signup => 
                    signup.participant_name?.toLowerCase().includes(attendanceSearchQuery.toLowerCase())
                  )
                  .map((signup) => {
                  const existingAttendance = calendarAttendance[selectedDateForAttendance]?.find(
                    att => att.signup_id === signup.id
                  );
                  
                  return (
                    <div key={signup.id} className="border rounded-lg p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm sm:text-base">{signup.participant_name}</h4>
                          <p className="text-xs sm:text-sm text-gray-500">{signup.program_name}</p>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <button
                            onClick={async () => {
                              await markAttendanceFromCalendar(signup.id, selectedDateForAttendance, true);
                            }}
                            disabled={markingAttendance || processingAttendance.has(`${signup.id}-${selectedDateForAttendance}-${true}`) || markedAttendance.has(`${signup.id}-${selectedDateForAttendance}-${true}`)}
                            className={`flex-1 sm:flex-none px-4 py-2.5 sm:py-2 rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${
                              existingAttendance && existingAttendance.attended ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            ✓ Present
                          </button>
                          <button
                            onClick={async () => {
                              await markAttendanceFromCalendar(signup.id, selectedDateForAttendance, false);
                            }}
                            disabled={markingAttendance || processingAttendance.has(`${signup.id}-${selectedDateForAttendance}-${false}`) || markedAttendance.has(`${signup.id}-${selectedDateForAttendance}-${false}`)}
                            className={`flex-1 sm:flex-none px-4 py-2.5 sm:py-2 rounded-lg font-medium active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed ${
                              existingAttendance && !existingAttendance.attended ? 'bg-red-600 text-white' : 
                              !existingAttendance ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            ✗ Absent
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-3 mt-6 sticky bottom-0 bg-white pt-4 border-t">
                <button
                  onClick={() => {
                    setShowMarkAttendanceModal(false);
                    setSelectedDateForAttendance(null);
                  }}
                  className="flex-1 px-4 py-3 sm:py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300 active:bg-gray-400 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showTermInvoiceModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">Generate Term Invoice - Select User</h3>
              
              {usersWithTermSignups.length === 0 ? (
                <p className="text-gray-500">No users with term signups found.</p>
              ) : (
                <div className="space-y-4">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
                  >
                    <option value="">Select a user...</option>
                    {usersWithTermSignups.map((user) => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.user_name} ({user.user_email}) - {user.children_count} child{user.children_count !== 1 ? 'ren' : ''}
                      </option>
                    ))}
                  </select>
                  
                  {selectedUserId && (
                    <div className="border-t border-gray-200 pt-4">
                      {usersWithTermSignups.find(u => u.user_id === selectedUserId) && (
                        <div>
                          <h4 className="font-semibold mb-2">Children:</h4>
                          <div className="space-y-2">
                            {usersWithTermSignups.find(u => u.user_id === selectedUserId).children.map((child, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-3">
                                <p className="font-medium">{child.participant_name}</p>
                                <p className="text-sm text-gray-600">{child.program_name}</p>
                                <p className="text-xs text-gray-500">Fee type: {child.fee_type}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowTermInvoiceModal(false);
                    setSelectedUserId('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateTermInvoiceForUser}
                  disabled={!selectedUserId || usersWithTermSignups.length === 0}
                  className="flex-1 bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Generate Invoice
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrograms;